import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useMemo, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  PlansTable,
  subscriptionsKeys,
  type PlanRow,
} from '~/features/subscriptions'
import { http } from '~/lib/http'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'
import type { AppError } from '~/lib/result'

/**
 * `/admin/subscriptions/plans` — plans list (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `status`.
 *
 * The `/plans` endpoint is small (<100 rows in practice) — pagination
 * here is mostly defensive for when add-on bundles ship and the count
 * grows. Filter chip → status pushes to URL state.
 */
const plansSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
  status: z.enum(['all', 'active', 'hidden', 'archived']).default('all'),
})
type PlansSearch = z.infer<typeof plansSearchSchema>

const URL_DEFAULTS: PlansSearch = {
  page: 1,
  limit: 25,
  status: 'all',
}

export const Route = createFileRoute('/admin/subscriptions/plans/')({
  validateSearch: plansSearchSchema,
  component: PlansListPage,
})

interface PlansListPayload {
  readonly plans?: readonly PlanRow[]
  readonly items?: readonly PlanRow[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchPlansPage(
  params: PaginatedParams,
): Promise<PaginatedResult<PlanRow>> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.sort) query.sort = params.sort
  if (params.filters?.status && params.filters.status !== 'all') {
    query.status = params.filters.status
  }

  const raw = await http.get<PlansListPayload>(
    '/api/v1/admin/subscriptions/plans',
    { query },
  )
  const rows = (raw.plans ?? raw.items ?? []) as readonly PlanRow[]
  // The /plans endpoint historically returns the full list without a
  // count. If the response is unpaginated we fall back to the row count
  // — that's correct for the small list today and forward-compatible.
  const total = raw.pagination?.total ?? raw.total ?? rows.length
  return toPaginatedResult(rows, total, params, raw.pagination)
}

function PlansListPage(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(plansSearchSchema, URL_DEFAULTS)
  const navigate = useNavigate()

  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    filters: { status: search.status },
  }

  const plansList = usePaginatedQuery<PlanRow>({
    queryKey: [...subscriptionsKeys.plans(), 'paginated'],
    params,
    fetcher: fetchPlansPage,
  })

  const apiError = plansList.error as AppError | null
  const isPermissionDenied =
    apiError?.code === 'forbidden' || apiError?.code === 'unauthorized'

  const allPlans = useMemo(
    () => plansList.data?.data ?? [],
    [plansList.data],
  )

  // Status filter is also applied client-side defensively in case the
  // legacy /plans endpoint ignores the query param.
  const filtered = useMemo(() => {
    if (search.status === 'all') return allPlans
    return allPlans.filter((p) => {
      const status = p.status ?? (p.is_active ? 'active' : 'hidden')
      return status === search.status
    })
  }, [allPlans, search.status])

  const counts = useMemo(() => {
    let active = 0
    let hidden = 0
    let archived = 0
    for (const p of allPlans) {
      const status = p.status ?? (p.is_active ? 'active' : 'hidden')
      if (status === 'active') active++
      else if (status === 'archived') archived++
      else hidden++
    }
    return { active, hidden, archived }
  }, [allPlans])

  const pagination = plansList.data?.pagination

  if (isPermissionDenied) {
    return (
      <div className="ka-card">
        <div className="ka-empty">
          <div className="ka-empty-title">Restricted</div>
          <div className="ka-empty-body">
            Request <code>subscription_management</code> from a super-admin.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="ka-subs-list-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Plans</h1>
          <p className="ka-page-header-subtitle">
            {pagination?.total ?? allPlans.length} plans · {counts.active}{' '}
            active · {counts.hidden} hidden · {counts.archived} archived
          </p>
        </div>
        <div className="ka-page-header-actions">
        <Link
          to="/admin/subscriptions/plans/$planId"
          params={{ planId: 'new' }}
          className="ka-btn ka-btn--primary"
        >
          <Plus size={14} aria-hidden="true" /> New Plan
        </Link>
        </div>
      </header>

      <div className="ka-filter-strip" role="search">
        <div className="ka-filter-group" role="group" aria-label="Status">
          {(['all', 'active', 'hidden', 'archived'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`ka-detail-chip${search.status === s ? ' is-active' : ''}`}
              aria-pressed={search.status === s}
              onClick={() => setUrl({ status: s, page: 1 })}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <PlansTable
        plans={filtered}
        isLoading={plansList.isPending}
        error={plansList.error}
        hasFilters={search.status !== 'all'}
        onClearFilters={() => setUrl({ status: 'all', page: 1 })}
        onCreatePlan={() =>
          void navigate({
            to: '/admin/subscriptions/plans/$planId',
            params: { planId: 'new' },
          })
        }
      />
      <DataTablePagination
        className="ka-subs-list-pagination"
        page={pagination?.page ?? search.page}
        limit={pagination?.limit ?? search.limit}
        total={pagination?.total ?? filtered.length}
        isLoading={plansList.isPending}
        onChange={({ page, limit }) => setUrl({ page, limit })}
      />
    </div>
  )
}
